import { useEffect, useMemo, useState } from "react";
import { availableCleaners, AvailableCleaner } from "@/mocks/admin";

type Props = {
  open: boolean;
  bookingId: string | null;
  currentCleanerId: string | null;
  service: string;
  time: string;
  dateLabel: string;
  city: string;
  onClose: () => void;
  onAssign: (cleaner: AvailableCleaner) => void;
};

const filters: { id: "all" | "available" | "busy"; label: string }[] = [
  { id: "all", label: "All" },
  { id: "available", label: "Available now" },
  { id: "busy", label: "Busy / off" },
];

const availabilityChip: Record<AvailableCleaner["availability"], string> = {
  available: "bg-primary-100 text-primary-800",
  busy: "bg-accent-100 text-accent-800",
  off: "bg-background-200 text-foreground-700",
};

const availabilityDot: Record<AvailableCleaner["availability"], string> = {
  available: "bg-primary-500",
  busy: "bg-accent-500",
  off: "bg-foreground-400",
};

export default function AssignCleanerModal({
  open,
  bookingId,
  currentCleanerId,
  service,
  time,
  dateLabel,
  city,
  onClose,
  onAssign,
}: Props) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"all" | "available" | "busy">("available");
  const [selectedId, setSelectedId] = useState<string | null>(currentCleanerId);
  const [notify, setNotify] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedId(currentCleanerId);
      setQuery("");
      setFilter("available");
      setNotify(true);
    }
  }, [open, currentCleanerId]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    return availableCleaners.filter((c) => {
      if (filter === "available" && c.availability !== "available") return false;
      if (filter === "busy" && c.availability === "available") return false;
      if (query.trim()) {
        const q = query.toLowerCase();
        return (
          c.name.toLowerCase().includes(q) ||
          c.role.toLowerCase().includes(q) ||
          c.specialties.some((s) => s.toLowerCase().includes(q))
        );
      }
      return true;
    });
  }, [query, filter]);

  const selected = availableCleaners.find((c) => c.id === selectedId) || null;

  if (!open) return null;

  const handleAssign = () => {
    if (!selected) return;
    setSubmitting(true);
    setTimeout(() => {
      onAssign(selected);
      setSubmitting(false);
    }, 600);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
      <div
        className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      <div className="relative w-full sm:max-w-2xl bg-background-50 sm:rounded-2xl rounded-t-2xl overflow-hidden flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="px-5 md:px-6 py-4 md:py-5 border-b border-background-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary-700">
              Assign cleaner
            </p>
            <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mt-0.5 truncate">
              {bookingId} · {service}
            </h3>
            <p className="text-xs text-foreground-600 mt-1">
              {dateLabel} at {time} · {city}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 cursor-pointer flex-shrink-0"
            aria-label="Close"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Search + filters */}
        <div className="px-5 md:px-6 pt-4 pb-3 space-y-3">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search cleaners or specialties…"
              className="w-full bg-background-100 placeholder:text-foreground-500 text-sm text-foreground-900 rounded-lg pl-9 pr-3 py-2.5 outline-none focus:bg-background-50 focus:ring-2 focus:ring-primary-300 transition"
            />
          </div>
          <div className="flex items-center gap-1 bg-background-100 p-1 rounded-full w-full sm:w-fit">
            {filters.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`flex-1 sm:flex-initial text-[11px] px-3 py-1.5 rounded-full font-medium whitespace-nowrap cursor-pointer transition ${
                  filter === f.id
                    ? "bg-foreground-950 text-background-50"
                    : "text-foreground-700 hover:text-foreground-950"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-5 md:px-6 pb-4">
          {filtered.length === 0 ? (
            <div className="py-12 text-center">
              <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-500">
                <i className="ri-team-line text-xl"></i>
              </span>
              <p className="text-sm font-medium text-foreground-900 mt-3">
                No cleaners match
              </p>
              <p className="text-xs text-foreground-600 mt-1">
                Try a different filter or search term.
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {filtered.map((c) => {
                const isSelected = selectedId === c.id;
                const isCurrent = currentCleanerId === c.id;
                return (
                  <li key={c.id}>
                    <button
                      onClick={() => setSelectedId(c.id)}
                      disabled={c.availability === "off"}
                      className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border transition cursor-pointer ${
                        isSelected
                          ? "border-primary-500 bg-primary-50/60 ring-2 ring-primary-200"
                          : "border-background-200 hover:border-foreground-300 hover:bg-background-100/60"
                      } ${c.availability === "off" ? "opacity-60 cursor-not-allowed" : ""}`}
                    >
                      <div className="relative flex-shrink-0">
                        <img
                          src={c.image}
                          alt={c.name}
                          className="w-12 h-12 rounded-full object-cover object-top"
                        />
                        <span
                          className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-background-50 ${availabilityDot[c.availability]}`}
                        ></span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-foreground-950 truncate">
                            {c.name}
                          </p>
                          {isCurrent && (
                            <span className="text-[10px] font-semibold uppercase tracking-wider bg-secondary-100 text-secondary-800 px-1.5 py-0.5 rounded-full">
                              Current
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-foreground-600 truncate">{c.role}</p>
                        <div className="flex items-center gap-3 mt-1 text-[11px] text-foreground-600">
                          <span className="flex items-center gap-0.5">
                            <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                            {c.rating}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-calendar-line"></i>
                            {c.jobsThisWeek} jobs/wk
                          </span>
                          <span className="hidden sm:flex items-center gap-1">
                            <i className="ri-map-pin-line"></i>
                            {c.distance}
                          </span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 hidden sm:block">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${availabilityChip[c.availability]}`}
                        >
                          {c.availability === "available"
                            ? "Available"
                            : c.availability === "busy"
                            ? "Busy"
                            : "Off"}
                        </span>
                        <p className="text-[10px] text-foreground-500 mt-1 max-w-[140px]">
                          {c.nextSlot}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 md:px-6 py-4 border-t border-background-200 bg-background-100/40">
          <label className="flex items-center gap-2 mb-3 cursor-pointer">
            <input
              type="checkbox"
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
              className="w-4 h-4 rounded border-background-300 text-primary-600 focus:ring-primary-400 cursor-pointer"
            />
            <span className="text-xs text-foreground-700">
              Notify cleaner and customer by SMS + email
            </span>
          </label>
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-2">
            <p className="text-[11px] text-foreground-500">
              {selected
                ? `Assigning ${selected.name}`
                : "Pick a cleaner to continue"}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="flex-1 sm:flex-initial px-4 py-2 rounded-full text-sm font-medium text-foreground-800 bg-background-50 border border-background-200 hover:bg-background-100 whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                disabled={!selected || submitting}
                className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 px-5 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i> Assigning…
                  </>
                ) : (
                  <>
                    <i className="ri-user-add-line"></i> Confirm assignment
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}